/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  <skio-plan-picker 
    product='null'
    selectedVariant='null'
    formId=''
    >
  </skio-plan-picker>
  <script src="//skooncatlitter.com/cdn/shop/t/97/assets/skio-plan-picker-component.js?v=81400891963702869161789213733" type="module"></script>

  Note: formId not required if element is inside of a form already
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioFreeGiftImageUrl = 'https://cdn.shopify.com/s/files/1/0612/4099/9988/files/image_4.png?v=1787638264';
const skioBogoImageUrl = 'https://cdn.shopify.com/s/files/1/0612/4099/9988/files/bogo_icon_2.png?v=1788350474';

// Subscription benefits, one per line in Theme settings. Split here rather than
// in Liquid so a CRLF from the textarea does not leave a stray \r on each item.
const skioSubscriptionBenefits = "10% Off Forever\nFree Shipping Forever\nCancel Anytime"
  .split(/\r?\n/)
  .map(function (benefit) { return benefit.trim(); })
  .filter(function (benefit) { return benefit.length > 0; });

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0;
    border: 0;
    max-width: 100%;
    margin-bottom: 10px;
    font-family: Urbanist, sans-serif;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    box-sizing: border-box;
    padding: 14px;
    border-radius: 12px;
    border: 1px solid #ccc;
    background-color: #fff;
    color: #767676;
    transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  }
  .skio-group-container--selected {
    border: 2px solid #82be41;
    background-color: rgba(130, 190, 65, 0.05);
    color: #82be41;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 12px;
  }

  .skio-group-label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    cursor: pointer;
  }

  .skio-group-topline {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .skio-radio__container {
    display: flex;
  }

  .skio-radio {
    transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-group-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    color: #000;
  }

  .skio-price-row {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 12px;
  }

  .skio-price {
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    color: #000;
  }
  .skio-group-container--selected .skio-price {
    color: #82be41;
  }

  .skio-compare-price {
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;
    color: #000;
    text-decoration: line-through;
  }

  .skio-benefits {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .skio-benefits li {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: #3b464c;
  }

  .skio-benefits li::before {
    content: "";
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #5bb134;
    /* Check mark, so no icon asset is needed inside the shadow root. */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
    background-position: center;
    background-size: 10px;
    background-repeat: no-repeat;
  }

  .skio-bogo-note {
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: #5bb134;
  }

  .skio-renewal-note {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: #767676;
  }

  /* Promo badges, laid out in a row so they cannot overlap each other. */
  .skio-badges {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 70%;
    pointer-events: none;
    z-index: 1;
  }

  .skio-badges img {
    display: block;
    height: 68px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
  }

  @media (max-width: 480px) {
    .skio-badges img {
      height: 52px;
    }
  }

  .skio-group-content {
    display: block;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;
    max-height: 44px;
    opacity: 1;
    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1);
  }

  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected) .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 100px;
    background-color: #fff;
    border: 1px solid #efefef;
    width: 100%;
    box-sizing: border-box;
    font-size: 14px;
    color: #000;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 16px top 50%;
    background-size: 14px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    productHandle: { type: String },      //optional (unless product isn't passed, then required)
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the Skio plan picker
    
    formId: { type: String },             //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean },       //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object
  
    selectedVariant: { type: Object },    //placeholder for data
    skioSellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data

    defaultFrequency: {},                 //placeholder for data

    quantity: { type: Number },           //placeholder for data; current bag quantity
    volumeTiers: { type: String },        //optional; "<bags>:<dollars off the line>", comma separated
    bogoEligible: { type: Boolean },      //optional, defaults to false; shows the BOGO badge and note
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.quantity = 1;
    this.volumeTiers = '';
    this.bogoEligible = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.currency = 'USD';
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;

  }

  connectedCallback() {
    super.connectedCallback();

    console.log("SKIO RENDERED");

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener("quantityChanged", function(e) {
      const quantity = parseInt(e.detail.quantity);
      if (!isNaN(quantity) && quantity > 0) {
        skio.quantity = quantity;
        skio.requestUpdate();
        skio.updatePerBagPrices();
      }
    })

    document.addEventListener("variantChanged", function(e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find(x => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log("Unable to find variant with id: ", variantId);



      skio.requestUpdate();
    })

  }

  render() {
    if(!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0) return;
    
    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="selling_plan" type="hidden" value="${ this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}" />
        <input ${ this.formId !== null ? html`form="${ this.formId }"` : '' } name="properties[Discount]" type="hidden" value="${ this.selectedSellingPlan !== null ? this.discount(this.selectedSellingPlan).percent : '' }" 
          ?disabled="${ this.selectedSellingPlan == null ? true : false }" />
        
        <div class="skio-group-container ${ this.product.requires_selling_plan == false ? 'skio-group-container--available' : '' } ${ this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : '' } ${ this.subscriptionFirst ? 'skio-onetime-second' : ''}" skio-group-container
          @click=${() => this.selectSellingPlanGroup(null) } >

          <input id="skio-one-time-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }" type="radio" value=""
            skio-one-time ?checked=${ this.startSubscription == false && this.product.requires_selling_plan == false ? true : false }>
          <label class="skio-group-label" for="skio-one-time-${ this.key }">
            <div class="skio-group-topline">
              <div class="skio-radio__container">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                  <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                </svg>
              </div>
              <div class="skio-group-title">One-Time Purchase</div>
            </div>
            <div class="skio-price-row">
              <span class="skio-price" skio-onetime-price>${ this.money(this.lineTotal(this.onetimePrice())) }</span>
            </div>
          </label>
        </div>
        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) =>
          html`
            <div class="skio-group-container skio-group-container--available ${ this.selectedSellingPlanGroup == group ? 'skio-group-container--selected' : '' }" skio-group-container
              @click=${() => this.selectSellingPlanGroup(group) }>
              <div class="skio-badges">
                ${ this.bogoEligible ? html`
                  <img class="skio-bogo" src="${ skioBogoImageUrl }" alt="Buy one get one free" loading="lazy">
                ` : html`` }
                <img class="skio-free-gift" src="${ skioFreeGiftImageUrl }" alt="Free gift with subscription" loading="lazy">
              </div>
              <input id="skio-selling-plan-group-${ index }-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }"
                type="radio" value="${ group.id }" skio-selling-plan-group="${ group.id }" ?checked=${ this.selectedSellingPlanGroup == group ? true : false } >
              <label class="skio-group-label" for="skio-selling-plan-group-${ index }-${ this.key }">
                <div class="skio-group-topline">
                  <div class="skio-radio__container">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                      <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                    </svg>
                  </div>
                  <div class="skio-group-title">${ group.name == 'Subscription' ? 'Subscribe & Save' : group.name }</div>
                </div>
                <div class="skio-price-row">
                  <span class="skio-price" skio-subscription-price>${ this.money(this.lineTotal(this.subscriptionPrice(group.selected_selling_plan))) }</span>
                  ${ this.displayDiscount(group.selected_selling_plan).amount > 0 ? html`
                    <span class="skio-compare-price">${ this.money(this.lineTotal(this.onetimePrice())) }</span>
                  ` : html`` }
                </div>
                ${ this.bogoEligible ? html`
                  <div class="skio-bogo-note">+1 Bag FREE with your first subscription order</div>
                ` : html`` }
                ${ this.renewalPrice(group.selected_selling_plan) !== null
                  && this.renewalPrice(group.selected_selling_plan) !== this.subscriptionPrice(group.selected_selling_plan) ? html`
                  <div class="skio-renewal-note">Renewals ${ this.money(this.lineTotal(this.renewalPrice(group.selected_selling_plan))) }</div>
                ` : html`` }
                ${ skioSubscriptionBenefits.length > 0 ? html`
                  <ul class="skio-benefits">
                    ${ skioSubscriptionBenefits.map((benefit) => html`<li>${ benefit }</li>`) }
                  </ul>
                ` : html`` }
                <div class="skio-group-content">
                  <select skio-selling-plans="${ group.id }" class="skio-frequency ${ group.selling_plans.length == 1 ? 'skio-frequency--one' : '' }"
                    @change=${ (e) => this.selectSellingPlan(e.target, group) }>
                    ${ group ? group.selling_plans.map((selling_plan) =>
                      html`
                      <option value="${ selling_plan.id }" ?selected=${group.selected_selling_plan == selling_plan }>
                        ${ group.name == 'Subscription' ? `Delivery ${ selling_plan.name.toLowerCase() }` : `${ selling_plan.name }` }
                      </option>
                      `
                    ): ''}
                  </select>
                </div>
              </label>
            </div>
          `
        ): ''}
      </fieldset>`
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO'
      )

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function(a,b){
          if (parseInt(a.name.replace(/\D/g, "")) < parseInt(b.name.replace(/\D/g, ""))) return -1;
          if (parseInt(a.name.replace(/\D/g, "")) > parseInt(b.name.replace(/\D/g, ""))) return 1;
          if (parseInt(a.name.replace(/\D/g, "")) == parseInt(b.name.replace(/\D/g, ""))) return 0;
        })
      });

    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group => {
          if (this.defaultFrequency) {
            console.log("SKIO DEFAULT FREQUENCY: ", this.defaultFrequency)
            let selling_plan = group.selling_plans.find(x => x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()));
            console.log("selling plan: ", selling_plan);
            if (selling_plan) {
              group.selected_selling_plan = selling_plan;
              group.selling_plans = [selling_plan];
            }
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
        }));

        if (this.startSubscription == true || this.product.requires_selling_plan == true || this.purchaseOption == 'subscription') {
          //find a matching selling plan, or choose first available
          if (this.selectedSellingPlan) {
            let sellingPlanName = this.selectedSellingPlan.name;
            let sellingPlanGroup = this.availableSellingPlanGroups.find(x => x.selling_plans.find(y => y.name == sellingPlanName));
            let sellingPlan = sellingPlanGroup.selling_plans.find(y => y.name == sellingPlanName);
            if (sellingPlanName == sellingPlan.name) {
              this.selectedSellingPlanGroup = sellingPlanGroup;
              this.selectedSellingPlan = sellingPlan;
            }
          }

          if (this.selectedSellingPlanGroup == null && this.selectedSellingPlan == null) {
            this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
            this.selectedSellingPlan = this.availableSellingPlanGroups[0].selected_selling_plan;
          }

        } else {
          this.selectedSellingPlan, this.selectedSellingPlanGroup = null;
        }
      } else {
        this.selectedSellingPlan, this.selectedSellingPlanGroup = null;
      }

      //update the form that was passed, if any
      this.updateForm();

    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      this.updatePerBagPrices();

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();

    }

    if(changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }

  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  }
  
  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    console.log("select selling plan group: ", group);
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (group) this.purchaseOption = 'subscription'
    else this.purchaseOption = 'onetime'

    //update the form that was passed, if any
    this.requestUpdate();
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Skio owns the real volume pricing and only applies it in the cart, so this
  // mirrors it for display. Parsed from the setting string rather than in
  // Liquid, which keeps the Liquid side to a single attribute.
  parsedVolumeTiers() {
    const tiers = {};

    (this.volumeTiers || '').split(',').forEach(function (pair) {
      const parts = pair.split(':');
      if (parts.length !== 2) return;

      const bags = parseInt(parts[0], 10);
      const dollarsOffLine = parseFloat(parts[1]);
      if (!bags || bags < 1 || isNaN(dollarsOffLine)) return;

      tiers[bags] = Math.round(dollarsOffLine * 100);
    });

    return tiers;
  }

  // Cents off each unit at a given bag count. Matched exactly, not as a
  // threshold, because the Skio rules are configured as "Exactly N items".
  tierUnitDiscount(quantity) {
    const offLine = this.parsedVolumeTiers()[quantity];
    if (!offLine) return 0;

    return Math.round(offLine / quantity);
  }

  // One-time unit price. Flat at any quantity: Skio has "Apply to one-time
  // products" off, so buying several bags outright earns no volume discount.
  onetimePrice() {
    return this.selectedVariant.price;
  }

  // Unit price at a given bag count for whichever purchase option is selected.
  // Volume tiers are subscription only, hence the guard.
  unitPriceAt(quantity) {
    if (!this.selectedSellingPlan) return this.onetimePrice();

    return Math.max(0, this.price(this.selectedSellingPlan, false) - this.tierUnitDiscount(quantity));
  }

  // Per-bag price on each quantity option. Every element declares the bag count
  // it stands for, so a tile shows its own tier price rather than the price of
  // whatever happens to be selected - which is what makes the three tiles
  // differ while a volume discount exists.
  updatePerBagPrices() {
    if (!this.selectedVariant) return;

    document.querySelectorAll('[data-per-bag-qty]').forEach((el) => {
      const bags = parseInt(el.getAttribute('data-per-bag-qty'), 10);
      if (!bags || bags < 1) return;

      el.innerHTML = this.money(this.unitPriceAt(bags));
    });
  }

  // Subscription unit price on the first order, at the selected bag count.
  subscriptionPrice(selling_plan) {
    return Math.max(0, this.price(selling_plan, false) - this.tierUnitDiscount(this.quantity));
  }

  // What the buyer pays for the selected number of bags. The unit-price helpers
  // stay the source of truth for the maths; quantity is applied at display time.
  lineTotal(unitPrice) {
    if (unitPrice === null) return null;
    return unitPrice * this.quantity;
  }

  // Selling plans can carry an introductory adjustment (order_count: 1) plus an
  // ongoing one (order_count: null). The headline price uses price_adjustments[0],
  // i.e. the introductory one, so the ongoing price is disclosed separately.
  recurringAdjustment(selling_plan) {
    if (!selling_plan || !selling_plan.price_adjustments) return null;

    const ongoing = selling_plan.price_adjustments.filter(
      (adjustment) => adjustment.order_count === null || adjustment.order_count === undefined
    );

    return ongoing.length > 0 ? ongoing[ongoing.length - 1] : null;
  }

  // Unit price on renewal orders, or null when every order costs the same.
  renewalPrice(selling_plan) {
    const adjustment = this.recurringAdjustment(selling_plan);
    if (!adjustment) return null;

    const price = this.selectedVariant.price;
    let planPrice = null;

    switch (adjustment.value_type) {
      case 'percentage':
        planPrice = price - Math.round((price * adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        planPrice = price - adjustment.value;
        break;
      case 'price':
        planPrice = adjustment.value;
        break;
    }

    if (planPrice === null) return null;

    return Math.max(0, planPrice - this.tierUnitDiscount(this.quantity));
  }

  // Saving between the two prices the picker actually shows, for the badge only.
  // discount() is left alone because it also feeds the properties[Discount] line
  // item property, which must keep reporting the plan's own discount.
  displayDiscount(selling_plan) {
    const full = this.onetimePrice();
    const amount = Math.max(0, full - this.subscriptionPrice(selling_plan));

    return {
      amount: amount,
      percent: full > 0 ? `${ Math.round((amount * 100.0) / full) }%` : '0%'
    };
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan)
      return { percent: '0%', amount: 0 }
    
    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price;
    
    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round(
          (price * price_adjustment.value) / 100.0
        )
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round(
          (((price - price_adjustment.value) * 1.0) /
            price) *
            100.0
        )}%`
        discount.amount = price - price_adjustment.value
        break
    }
    
    return discount
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    return formatted
      ? this.money( this.selectedVariant.price - this.discount(selling_plan).amount)
      :  this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (this.formId) {
      let form = document.querySelector(`#${this.formId}`);

      if (form) {
        let selling_plan_input = form.querySelector('[name="selling_plan"]');
        if (selling_plan_input) {
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        } else {
          selling_plan_input = document.createElement('input');
          selling_plan_input.type = "hidden";
          selling_plan_input.name = "selling_plan";
          selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
          selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
          form.append(selling_plan_input);
        }

        // let discount_input = form.querySelector('[name="properties[Discount]"]');
        // if (discount_input) {
        //   discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
        //   discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        // } else {
        //   discount_input = document.createElement('input');
        //   discount_input.type = "hidden";
        //   discount_input.name = "properties[Discount]";
        //   discount_input.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null;
        //   discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        //   form.append(discount_input);
        // }

      } else {
        console.log(`Skio error: form ID is ${ this.formId }, but no form with that ID was found.`);
      }
    }
  }

  // Optional functions keep if necessary 

  /**
   *   
   * 
   */
  
  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log("SKIO added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`skio::added-to-cart`, {
        bubbles: true, 
        composed: true, 
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(`SKIO ${ this.key } error adding item to cart: `, error);
    });
  } 

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }
}

customElements.define('skio-plan-picker-evs', SkioPlanPickerComponent);

document.addEventListener("DOMContentLoaded", function() {
  if (document.querySelectorAll(".shopify-product-form").length > 0) {
    let rechargeObserver = 
      new MutationObserver(function(mutationsList, observer) {
        for(const mutation of mutationsList) {
          document.querySelectorAll(".rc-container-wrapper").forEach((el) => el.remove())
        }
      }).observe(document.querySelector(".shopify-product-form"), { childList: true, subtree: true });
  }
})



